import { useEffect, useState } from "react";
import { Link, useSearch, useLocation } from "wouter";
import { Search as SearchIcon, SearchX, Loader2, Package, Wrench, Building2, Users, Briefcase, FolderKanban, ReceiptText } from "lucide-react";
import { SaveButton } from "../components/SaveButton";
import { AccountVerificationBadge } from "../components/AccountVerificationBadge";
import { ProfessionalBadges } from "../components/ProfessionalBadges";
import { accountVerificationFrom } from "../lib/accountVerification";
import { SearchInput } from "../components/SearchInput";
import { api } from "../lib/api";
import { MAX_SEARCH_QUERY_LENGTH } from "../lib/search";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { profileKey } from "../lib/profileSession";
import { useSavedMutationPending } from "../lib/saved";
import { PullToRefresh } from "../components/PullToRefresh";
import { refreshFamily, useRefreshOwner } from "../lib/useRefreshOwner";

const META: Record<string, { Icon: any; label: string }> = {
  item: { Icon: Package, label: "Item" }, service: { Icon: Wrench, label: "Service" },
  company: { Icon: Building2, label: "Company" }, person: { Icon: Users, label: "Jobseeker" }, profile: { Icon: Users, label: "Profile" },
  job: { Icon: Briefcase, label: "Job" }, project: { Icon: FolderKanban, label: "Project" }, rfq: { Icon: ReceiptText, label: "RFQ" },
};
const TYPE_ORDER = ["item", "service", "company", "person", "job", "project", "rfq"];

export default function Search() {
  const { lang, t } = useI18n();
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const q = params.get("q") || "";
  const type = params.get("type") || "";
  const [box, setBox] = useState(q);
  const [data, setData] = useState<any>(null);
  const queryTooLong = q.length > MAX_SEARCH_QUERY_LENGTH;
  const { user } = useAuth();
  const savedPending = useSavedMutationPending();
  const identity = `${profileKey(user)}:search:${q}:${type}`;

  useEffect(() => { setBox(q); }, [q]);
  useEffect(() => { setData(null); }, [q, type]);
  const refresh = useRefreshOwner(identity, signal => [refreshFamily(async () => {
    const response = await api.search(q, type || undefined, signal);
    if (!Array.isArray(response?.results) || !response.counts || !Number.isFinite(response.total)) throw new Error("Missing search results.");
    return response;
  }, setData, () => setData(null))], { enabled: q.trim().length >= 2 && !queryTooLong });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (box.trim().length >= 2 && box.length <= MAX_SEARCH_QUERY_LENGTH) navigate(`/search?q=${encodeURIComponent(box.trim())}`);
  }
  function setType(t: string) { navigate(`/search?q=${encodeURIComponent(q)}${t ? `&type=${t}` : ""}`); }

  const counts = data?.counts || {};
  const totalAll = Object.values(counts).reduce((a: number, b: any) => a + (b || 0), 0);

  return (
    <PullToRefresh identity={`${identity}:${savedPending}`} disabled={queryTooLong || q.trim().length < 2 || box.trim() !== q.trim() || savedPending || (refresh.reading && !data)} onRefresh={refresh.onRefresh}>
    <div className="container-x py-6 max-w-3xl">
      {refresh.error && <p role="alert" className="mb-3 text-sm text-brand-red">{refresh.error}</p>}
      <form onSubmit={submit} className="relative mb-5">
        <SearchInput
          id="unified-search-query"
          autoFocus
          className="field pl-11 h-12 text-base"
          iconClassName="w-5 h-5 absolute left-4 top-3.5 text-mut"
          value={box}
          onChange={setBox}
          placeholder={lang === "tl" ? t("discovery.keywords") : "Search people, services, companies, jobs, projects, items…"}
        />
      </form>

      {q.trim().length >= 2 && (
        <div className="flex gap-1.5 flex-wrap mb-4">
          <button onClick={() => setType("")} className={`chip ${!type ? "bg-navy text-white" : "bg-navy-50 text-navy-600 hover:bg-navy hover:text-white"}`}>All ({totalAll})</button>
          {TYPE_ORDER.filter((t) => (counts[t] || 0) > 0).map((t) => (
            <button key={t} onClick={() => setType(t)} className={`chip capitalize ${type === t ? "bg-navy text-white" : "bg-navy-50 text-navy-600 hover:bg-navy hover:text-white"}`}>{META[t].label} ({counts[t]})</button>
          ))}
        </div>
      )}

      {queryTooLong ? (
        <div className="card p-10 text-center text-brand-red">Search query cannot exceed {MAX_SEARCH_QUERY_LENGTH} characters.</div>
      ) : q.trim().length < 2 ? (
        <div className="card p-10 text-center text-mut"><SearchIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />Type at least 2 characters to search the marketplace.</div>
      ) : !refresh.loaded && !data?.results?.length ? (
        <div className="py-14 text-center text-mut"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : !data ? null : (data.results?.length ?? 0) === 0 ? (
        <div className="card p-10 text-center">
          <SearchX className="w-8 h-8 text-mut mx-auto mb-2" />
          <div className="font-bold text-navy">{lang === "tl" ? `${t("discovery.noResults")} — “${q}”` : `No results for “${q}”`}</div>
          <p className="text-sm text-mut mt-1">Try different keywords or fewer filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-mut mb-1">{data.results.length} result{data.results.length === 1 ? "" : "s"}{type ? ` in ${META[type]?.label}` : ""}</p>
          {data.results.map((r: any) => {
            const m = META[r.type] || { Icon: SearchIcon, label: r.type };
            const Icon = m.Icon;
            return (
              <div key={`${r.type}:${r.id}`} className="card p-3 flex items-center justify-between gap-3 hover:shadow-lift transition-shadow">
                <Link href={r.link} aria-label={`${t("discovery.viewDetails")}: ${r.title}`} className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-navy"><span className="truncate">{r.title}</span>{r.type === "person" || r.type === "profile" ? <AccountVerificationBadge {...accountVerificationFrom(r)} /> : r.verifiedProfessions?.length > 0 && <ProfessionalBadges professions={r.verifiedProfessions} compact />}</div>
                    <div className="text-xs text-mut truncate"><span className="chip bg-navy-50 text-navy-600 mr-1">{m.label}</span>{r.subtitle}</div>
                  </div>
                </Link>
                <SaveButton kind={r.type === "person" ? "profile" : r.type} refId={String(r.id)} className="h-9 w-9 shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
    </PullToRefresh>
  );
}
